import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Copy,
  RefreshCw,
  Mail,
  Loader2,
  ExternalLink,
  Users as UsersIcon,
  Send,
  CheckCircle2,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import InfluencerProspectsCRM, { INSTA_DM_TEMPLATE_A } from './admin/InfluencerProspectsCRM';
import { Instagram } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAIL_URL = `${API}/admin/mail-templates/influencer`;

const SUGGESTED_SUBJECTS = [
  '[Prénom] — 1 semaine d\'accès Expert offert pour tester TRADEX AI',
  '[Prénom], on aimerait te faire découvrir TRADEX AI (accès offert)',
  'Proposition partenariat TRADEX AI — [Prénom]',
  '[Prénom], 200+ analyses IA trackées publiquement — ça peut t\'intéresser',
];

// Text-only version — kept in sync with the HTML template
const buildPlainText = (stats) => `TRADEX AI
Un clic, Un trade.

---

Salut [Prénom],

Je suis Jérémy, fondateur de TRADEX AI. Ta façon d'expliquer le trading — simple, claire, avec ton expérience de terrain — correspond exactement au public qu'on veut aider : les débutants et intermédiaires qui veulent progresser sans se faire manipuler. D'où ce message.

En deux lignes : TRADEX AI est un copilote d'analyse graphique piloté par une IA qui prend en compte tous les paramètres du marché — actualité économique, 4 unités de temps, structure, momentum, liquidité, confluence, sessions Kill Zones — pour ne proposer que des setups qui respectent les bases du trading.

200+ analyses trackées publiquement. WIN et LOSS. Sans filtre. Jette un œil :
👉 https://tradex-ai.fr/track-record

---

🎯 ${stats?.duration || '5 mois'} de tracking public

• ${stats?.total_analyses || '138'} Analyses IA
• ${stats?.win_rate || '40'}% Trades gagnants
• ${stats?.avg_rr || '3.0'}:1 R:R moyen
• ${stats?.period_label || '10 avr. → 24 août'}

---

🔒 Ce que je te dis pas ici (volontairement)

• Comment notre Scan Auto détecte automatiquement les setups tous les jours sur tes assets
• Comment notre analyse IA déclenche une lecture multi-timeframe complète en un clic
• Le programme fondateur — 100 places, avantages exclusifs (il en reste)
• Notre programme de redevance partenaires — comment on rémunère les créateurs qui parlent de TRADEX AI

Ça se raconte mieux au téléphone qu'en mail.

---

🎁 Cadeau — 7 jours d'accès Expert offerts

Accès complet. Analyses IA illimitées, Scan Auto 10 actifs, alertes push, Track Record complet.
Aucune carte demandée. Aucun engagement.

Code promo : LAUNCH
👉 https://tradex-ai.fr/register

---

📞 Et si on se parlait ?

Le plus simple : demande à être rappelé en 2 clics.
Je t'appelle personnellement sous 24h ouvrées.

👉 https://tradex-ai.fr/rappel

Ou directement au +33 6 99 03 55 94 · WhatsApp : https://wa.me/33699035594

---

Peu importe qu'on collabore ou non — je veux surtout que tu voies ce qu'on fait. Un outil ICT sérieux made in France, ça manquait.

À très vite,

Jérémy Vermot
Fondateur — TRADEX AI
https://tradex-ai.fr

"Un clic, Un trade."
`;

export default function AdminMailTemplates() {
  const [html, setHtml] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);

  // Send form state
  const [sendForm, setSendForm] = useState({ to: '', first_name: '', subject: '' });
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(MAIL_URL, {
        headers: { Accept: 'text/html' },
        transformResponse: [(d) => d], // keep raw HTML
      });
      const rawHtml = typeof res.data === 'string' ? res.data : String(res.data);
      setHtml(rawHtml);

      // Extract stats from the rendered HTML for the text version + summary
      try {
        const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
        const monoNodes = [...doc.querySelectorAll('div')]
          .filter((d) => d.style?.fontFamily === 'monospace' && /^[\d.:%\s\-a-zA-Zéûôàè]+$/.test(d.textContent.trim()))
          .map((d) => d.textContent.trim());
        const [nb, wr, rr, dur] = monoNodes;
        const durationLabelNode = [...doc.querySelectorAll('div')]
          .find((d) => d.textContent && d.textContent.match(/\s*→\s*/));
        setStats({
          total_analyses: nb,
          win_rate: wr?.replace('%', ''),
          avg_rr: rr?.replace(':1', ''),
          duration: dur,
          period_label: durationLabelNode?.textContent?.trim(),
        });
      } catch (_e) {
        // parsing errors ignored — text version will use hardcoded defaults
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement du template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Inject raw HTML into iframe via srcdoc (isolates styles)
  useEffect(() => {
    if (iframeRef.current && html) {
      iframeRef.current.srcdoc = html;
    }
  }, [html]);

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copié dans le presse-papiers ✅`);
    } catch (err) {
      toast.error('Copie impossible — sélectionnez le texte manuellement');
    }
  };

  const plainText = buildPlainText(stats);
  const openInNewTab = () => window.open(MAIL_URL, '_blank', 'noopener');

  const sendMail = async () => {
    if (!sendForm.to) {
      toast.error('Renseigne l\'email du destinataire');
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/mail-templates/influencer/send`, {
        to: sendForm.to.trim(),
        first_name: sendForm.first_name.trim() || undefined,
        subject: sendForm.subject.trim() || undefined,
      });
      setLastSent({ to: res.data.to, subject: res.data.subject, at: new Date().toLocaleString('fr-FR') });
      toast.success(`Mail envoyé à ${res.data.to} ✅`);
      setSendForm({ to: '', first_name: '', subject: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Envoi impossible — vérifie la config Resend');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-mail-templates">
      <Card className="p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                Mail Démarchage Influenceurs
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
                  Live stats
                </Badge>
              </h2>
              <p className="text-xs text-muted-foreground">
                Les chiffres du Track Record sont injectés automatiquement à chaque chargement.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReloadKey((k) => k + 1)}
              disabled={loading}
              data-testid="mail-reload-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Régénérer
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openInNewTab}
              data-testid="mail-open-tab-btn"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" />
              Ouvrir dans un nouvel onglet
            </Button>
          </div>
        </div>

        {/* Stats summary */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 p-4 rounded-lg bg-white/5 border border-white/10">
            <MiniStat label="Analyses" value={stats.total_analyses} color="text-emerald-400" />
            <MiniStat label="Trades gagnants" value={`${stats.win_rate}%`} color="text-emerald-300" />
            <MiniStat label="R:R moyen" value={`${stats.avg_rr}:1`} color="text-orange-400" />
            <MiniStat label={stats.period_label} value={stats.duration} color="text-blue-400" />
          </div>
        )}

        {/* Direct send form — the KEY feature (no copy-paste needed) */}
        <div className="mb-6 p-4 rounded-lg border border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.03]" data-testid="mail-send-form">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Send className="w-4 h-4 text-emerald-400" />
            Envoyer directement le mail (recommandé — aucune manipulation de HTML)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <Label className="text-xs mb-1 block">Prénom de l'influenceur</Label>
              <Input
                value={sendForm.first_name}
                onChange={(e) => setSendForm((f) => ({ ...f, first_name: e.target.value }))}
                placeholder="Jean"
                data-testid="mail-send-firstname"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs mb-1 block">Email destinataire *</Label>
              <Input
                type="email"
                value={sendForm.to}
                onChange={(e) => setSendForm((f) => ({ ...f, to: e.target.value }))}
                placeholder="influenceur@example.com"
                data-testid="mail-send-to"
              />
            </div>
          </div>
          <div className="mb-3">
            <Label className="text-xs mb-1 block">Objet (facultatif — utilise le sujet par défaut si vide)</Label>
            <Input
              value={sendForm.subject}
              onChange={(e) => setSendForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="Ex : [Prénom] — 1 semaine d'accès Expert offert pour tester TRADEX AI"
              data-testid="mail-send-subject"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Astuce : tape <code className="bg-white/10 px-1 rounded">[Prénom]</code> dans le sujet, il sera remplacé automatiquement.
            </p>
          </div>
          <Button
            onClick={sendMail}
            disabled={sending || !sendForm.to}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
            data-testid="mail-send-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi via Resend...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Envoyer maintenant via Resend</>
            )}
          </Button>
          {lastSent && (
            <div className="mt-3 p-3 rounded bg-emerald-500/10 border border-emerald-500/20 text-xs flex items-center gap-2" data-testid="mail-send-success">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Dernier envoi : <strong>{lastSent.to}</strong> · Objet « {lastSent.subject} » · {lastSent.at}</span>
            </div>
          )}
        </div>

        {/* Suggested subjects */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-400" />
            Objets suggérés (cliquer pour copier)
          </h3>
          <div className="space-y-2" data-testid="mail-subjects-list">
            {SUGGESTED_SUBJECTS.map((s, i) => (
              <button
                key={i}
                onClick={() => copyToClipboard(s, 'Objet')}
                className="w-full text-left px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-foreground transition-colors flex items-center justify-between gap-3 group"
                data-testid={`mail-subject-${i}`}
              >
                <span className="truncate">{s}</span>
                <Copy className="w-3.5 h-3.5 text-muted-foreground group-hover:text-emerald-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* Copy actions — fallback if you prefer sending manually via another tool */}
        <div className="mb-2 text-xs text-muted-foreground uppercase tracking-widest">
          Ou copier le mail (si tu préfères envoyer depuis un autre outil)
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <Button
            onClick={() => copyToClipboard(html, 'HTML du mail')}
            variant="outline"
            className="border-white/20"
            disabled={!html || loading}
            data-testid="mail-copy-html-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copier le HTML (Resend, Mailchimp, etc.)
          </Button>
          <Button
            onClick={() => copyToClipboard(plainText, 'Version texte')}
            variant="outline"
            className="border-white/20"
            disabled={!html || loading}
            data-testid="mail-copy-text-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copier la version texte (Gmail direct)
          </Button>
        </div>
      </Card>

      {/* Instagram DM template — Version A (ultra-short, single message) */}
      <Card className="p-5 mb-4" data-testid="insta-dm-template">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
              <Instagram className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                DM Instagram — Version A
                <Badge className="bg-pink-500/15 text-pink-300 border-pink-500/30 text-[10px]">
                  Ultra-court
                </Badge>
              </h2>
              <p className="text-xs text-muted-foreground">
                Un seul message, tient sans « Voir plus ». Pour les influenceurs sans email pro trouvable.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => copyToClipboard(INSTA_DM_TEMPLATE_A, 'DM Instagram')}
            className="bg-pink-500 hover:bg-pink-600 text-white"
            data-testid="copy-insta-dm-btn"
          >
            <Copy className="w-4 h-4 mr-1.5" />
            Copier le DM
          </Button>
        </div>
        <pre
          className="whitespace-pre-wrap text-sm bg-white/5 border border-white/10 rounded-lg p-4 font-sans text-foreground/90"
          data-testid="insta-dm-preview"
        >
{INSTA_DM_TEMPLATE_A}
        </pre>
        <p className="text-[11px] text-muted-foreground mt-2">
          💡 Astuce : dans le CRM ci-dessous, cliquer sur l'icône <Copy className="w-3 h-3 inline" /> pour un prospect
          Instagram remplace automatiquement <code className="bg-white/10 px-1 rounded">[Prénom]</code> par son prénom
          et le marque « Contacté » avec la date du jour.
        </p>
      </Card>

      {/* Influencer Prospects CRM */}
      <div className="mb-4">
        <InfluencerProspectsCRM />
      </div>

      {/* Preview */}
      <Card className="overflow-hidden">
        <div className="p-3 border-b border-white/10 bg-white/[0.02] flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Aperçu du mail (rendu final)
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
        </div>
        <div className="bg-[#0a0a12]" data-testid="mail-preview-wrapper">
          <iframe
            ref={iframeRef}
            title="Mail influenceur"
            className="w-full block"
            style={{ height: '1400px', border: 0, background: '#0a0a12' }}
            data-testid="mail-preview-iframe"
          />
        </div>
      </Card>
    </div>
  );
}

const MiniStat = ({ label, value, color = 'text-foreground' }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`text-lg font-bold font-mono ${color}`}>{value}</div>
  </div>
);
